/**
 * A custom version of a validator that is consistent with the Kendo validator, but does not use the DOM.
 * Adds support for warning level messages, and allowing individual errors to be overridden.
 *
 * Intended to be used as a mixin into a widget or observable.  Uses trigger() and other methods that are
 * dependent on that intention.
 *
 * Requirements for those who wish to implement:
 * 1. Use as a mixin into a Widget.  See CustomPromptList for an example.  This gets us trigger support to throw events around.
 * 2. Listen for "validate" and "validateServer" events (either in the widget, or more likely, in the ViewModel that contains the widget).
 *    a. validate event is thrown when either the validate() or silentValidate() methods are called.
 *       The payload of the event tells you whether the form is valid.
 *    b. serverValidate event is thrown when server validation is needed.  This class is ignorant of calling the
 *       server to validate stuff, it relies on the implementation to handle the serverValidate event, call the server,
 *       and then report results back to the validator using the provided promise.  They event payload contains a
 *       promise that you can resolve if server validation completed, or reject if the server validation failed.
 *       When rejecting, pass the ValidationException as an argument.
 *       - fieldName of the validation error should match up to the options.validationField.  Defaults to NAME of the PromptInputModel (NOT the ID).
 *       - errorType of the validation error should be either ERROR or WARNING
 *       - errorMessage of the validation error should contain the actual message.
 * 3. validate, silentValidate, and validateInput all return promises
 * 4. The validator sets model fields validState and validMessage.  It does not modify any UI elements in any way.
 *    Implementing classes are responsible for listening to changes on the model and showing tooltips, notifications,
 *    or whatever other UI validation cues are desired.  validMessage is an array.
 *
 */
import locale from "common/managers/locale";
import ValidState from "common/enums/validators/ValidState";
import DataType from "common/enums/DataType";
import loginProps from "common/managers/loginProps";
import moment from "moment";
import dateConvert from "common/helper/dateConvert";
import ValidatorType from "common/enums/validators/ValidatorType";
import ObjectUtil from "common/helper/ObjectUtil";
import PromptInputType from "common/enums/PromptInputType";
import DisplayError from "common/error/DisplayError";
import join from "common/helper/join";
import stringUtil from "common/helper/stringUtil";

export default {

    // a datasource containing the PromptList fields - PromptInputModel
    dsPrompts: undefined,

    // current state of validation, only valid after validate or silentValidate have been called at least once.
    _valid: false,

    _minDate: undefined,
    _maxDate: undefined,

    //Should required validation be skipped.
    _skipRequired: false,

    // object that holds validation errors/warnings
    _results: {},

    /**
     * Whether the validator is in a valid state.  Factors in warning level validation, and override errors.
     * Only usable if either validate or silentValidate has been run.
     *
     * @return {boolean}
     */
    isValid: function() {
        return this._valid;
    },


    /**
     * Get/set whether to skip required field validation.  If true, the form will show required field indicators,
     * but will not enforce required field validation.
     *
     * @param skip if true, skip required field validation
     * @return {boolean}
     */
    skipRequired: function(skip) {
        if (skip === undefined) {
            return this._skipRequired;
        }
        this._skipRequired = skip;
        this.trigger("skipRequiredChange");
    },

    validateWithoutFocus(bypassRequiredValidation = false) {
        const before = this._skipRequired;
        this._skipRequired = (this._skipRequired || bypassRequiredValidation);
        return this._doValidate()
            .always(() => {
                this._skipRequired = before;
            });
    },

    /**
     * Validate all controls and show validation feedback.
     * @param bypassRequiredValidation Should required validators be skipped
     * @return {boolean|*}
     */
    validate: function(bypassRequiredValidation = false) {
        return this.validateWithoutFocus(bypassRequiredValidation)
            .always(() => {
                this.focusInvalid();
            });
    },

    focusInvalid() {
        this.promptWidgets?.find((prompt) => {
            return prompt.canFocus() && prompt.options.model.get("validState") === ValidState.ERROR;
        })?.focus();
    },

    _doValidate: function(silent) {
        var me = this,
            promises = [];
        if (this.dsPrompts) {
            // if we need to run server validation, group all validators together and run one time.
            var server = {
                run: false,
                fields: {}
            };
            this.dsPrompts.view().forEach(function(prompt) {
                promises.push(me._doValidateInput(prompt, silent, false, server));
            });

            // figure out if we need to run server validation
            if (server.run) {
                // when validating the entire form, we need to account for the possibility that the server
                // could return an error for a field that we don't expect it to.
                // in order for that to work, server.fields must have a deferred object for each field.
                this.dsPrompts.view().forEach(function(prompt) {
                    if (!server.fields[prompt.get(me.options.validationField)]) {
                        var def = $.Deferred();
                        server.fields[prompt.get(me.options.validationField)] = def;
                        promises.push(def.promise());
                        me._addValidationHandlers(def, prompt, silent);
                    }
                });

                this._validateServer(server);
            }
        }

        // when ALL promises resolve/reject, trigger a validate event.
        // We have to work around a jQuery limitation here - the .done() handler will not fire until all promises
        // have been resolved.  However, the .fail() handler will fire after the FIRST promise fails.
        // In cases where UI validation fails, but we also need to call the server, this produces incorrect results.
        // The index panel will not get a validate event, and it won't show the right messages in the notification.

        // To fix this, we are creating second promise objects and faking an "all settled" behavior that forces
        // the $.when logic to wait until all promises are either resolved or rejected before triggering our logic.
        var def = $.Deferred();
        $.when.apply($, $.map(promises, function(promise) {
                return promise.then(null, function() {
                    // on reject, resolve a promise with "false" for invalid.
                    return $.Deferred().resolve(false);
                })
            }))
            .done(function() {
                // no fail handler will be called in this case.  We need to iterate over the arguments to this callback.
                // If any of the arguments are false, at least one validator failed.

                // convert arguments to array for ease of use.
                var args = Array.prototype.slice.call(arguments);
                var valid = !args.some(function(arg) {
                    return arg === false;
                });

                // set valid flags and trigger event
                me._valid = valid;
                me.trigger("validate", {
                    sender: me,
                    valid: valid,
                    silent
                });

                // resolve or reject promise based on valid state
                if (valid) {
                    def.resolve();
                } else {
                    def.reject();
                }
            });
        return def.promise();
    },

    /**
     * Validate a single input.
     *
     * @param model the input model to validate
     * @param clientOnly if true, run only client-side validators such as required fields.
     */
    validateInput: function(model, clientOnly) {
        var server = {
            run: false,
            fields: {}
        };
        var ret = this._doValidateInput(model, false, clientOnly, server);

        // figure out if we need to run server validation
        if (server.run) {
            this._validateServer(server);
        }

        return ret;
    },

    /**
     * Trigger an event that will indicates server validation is needed.  The actual validation call happens elsewhere.
     * @param server object containing deferred objects for each field
     * @private
     */
    _validateServer: function(server) {
        var promise = $.Deferred(),
            me = this;

        this.validatingOnServer = true;

        server.promise = promise;
        this.trigger("validateServer", server);

        promise
            .done(function() {
                me._applyServerResults(undefined, server);
            })
            .fail(function(err) {
                me._applyServerResults(err, server);
            })
            .always(function() {
                me.validatingOnServer = false;
            });
    },

    /**
     * Validate a single input.
     *
     * @param model the input model to validate
     * @param silent whether or not to display feedback
     * @param clientOnly if true, run only client-side validators such as required fields.
     * @param server object that stores whether server validation needs to run, and if so, holds fields the server will validate.
     * @return {promise} returns a promise
     * @private
     */
    _doValidateInput: function(model, silent, clientOnly, server) {
        var me = this,
            valid = true,
            state = ValidState.VALID,
            errorIndices,
            messages = [],
            requireServer = false,
            def,
            rule,
            result,
            clientResults = [];

        // Always run client side validators like required, min/max, etc.
        for (rule in me._clientRules) {
            result = me._clientRules[rule].call(this, model);
            if (result.state != ValidState.VALID) {
                clientResults.push(result);

                // certain rules like required should stop further execution when invalid
                if (result.stop) {
                    break;
                }
            }
        }

        // process errors first, then warnings.
        clientResults.filter(function(err) {
            return err.state == ValidState.ERROR;
        }).forEach(function(result) {
            valid = model.get("errorsOverridden");
            messages.push(result.message);
            state = result.state;
            errorIndices = result.errorIndices;
        });

        // if no errors, look for warnings
        if (messages.length == 0) {
            clientResults.filter(function(err) {
                return err.state == ValidState.WARNING;
            }).forEach(function(result) {
                messages.push(result.message);
                state = result.state;
                errorIndices = result.errorIndices;
            });
        }

        // set a clientValid flag so we can use it later if server validation runs.
        model.clientValid = valid;

        // if the client rules all pass, proceed to run server validation.
        if (!clientOnly && valid) {
            // for server validation, all we need to determine is whether at least one server rule is present.
            // if so, we can stop right away, and call the server validation.
            for (rule in me._serverRules) {
                if (me._serverRules[rule].call(this, model)) {
                    requireServer = true;
                    server.run = true;
                    server.silent = silent;
                    break;
                }
            }
        }

        // If we do not require server validation, or if we're told to skip it, resolve immediately.
        // Otherwise, run server validation.
        if (clientOnly || !requireServer) {
            def = $.Deferred();

            // special case here - if client only, check to see whether this model has a serverInvalid flag set.
            // if so, and it's true, update the valid flag to false.
            // This fixes clientOnly validation that happens on focus out of a field that previously showed
            // a server validation error.
            if (valid && clientOnly && model.get("serverInvalid")) {
                valid = model.get("validState") === ValidState.WARNING;
                state = model.get("validState");
                messages = model.get("validMessage");
            }

            if (valid) {
                def.resolve(state, messages, errorIndices);
            } else {
                def.reject(state, messages, errorIndices)
            }
        } else {
            // run server validation.  To fit with our requirement of calling the server once, but still be able to
            // program against Deferred objects, we will store this model and a deferred that can be evaluated later.
            def = $.Deferred();
            server.fields[model.get(me.options.validationField)] = def;
        }

        me._addValidationHandlers(def, model, silent);
        return def.promise();
    },

    _addValidationHandlers: function(def, model, silent) {
        var me = this;
        def
            .done(function(state, message) {
                // "valid".  Because we have warning level validators, the done handler can mean 2 things:
                // 1. the input was totally valid.  Clear the validMessage, remove this field from the results cache.
                // 2. the input has a warning.  Set the validMessage, add to results cache so errors() method returns data.

                // In cases where the validation was requested from the server, some other piece of code is
                // resolving the deferred.  The contract is that it should send a state and message.
                me._markFieldValid(model, state, message, silent);
            })
            .fail(function(state, message, errorIndices) {
                // invalid
                // In cases where the validation was requested from the server, some other piece of code is
                // rejecting the deferred.  The contract is that it should send a state and message.
                me._markFieldInvalid(model, state, message, silent, errorIndices);
            });
    },

    _markFieldValid: function(model, state, message, silent) {
        model.set("validState", state);
        if (state == ValidState.VALID || model.get("errorsOverridden")) {
            delete this._results[model.get("name")];
        } else {
            this._results[model.get("name")] = {
                state: state,
                message: message,
                label: model.get("label")
            };
        }
        if (!silent) {
            if (state == ValidState.VALID) {
                model.set("validMessage", "");
            } else {
                model.set("validMessage", message)
            }
        }
    },

    _markFieldInvalid: function(model, state, message, silent, errorIndices) {
        model.errorIndices = errorIndices;
        model.set("validState", state);

        this._results[model.get("name")] = {
            state: state,
            message: message,
            label: model.get("label")
        };

        if (!silent) {
            model.set("validMessage", message);
        }
    },

    /**
     * Validates the full form without modifying the UI in any way.  Allows validation on read-only and disabled inputs.
     */
    silentValidate: function() {
        return this._doValidate(true);
    },

    /**
     * Get all error messages.
     */
    errors: function() {
        var result = [],
            name,
            error;
        for (name in this._results) {
            error = this._results[name];
            if (!error.message) {
                error.message = [];
            }
            var message = error.message.filter(function(msg) {
                return !!msg;
            });

            if (message.length > 0) {
                result.push({
                    field: error.label || name,
                    state: error.state,
                    message: message
                });
            }
        }
        return result;
    },

    /**
     * Hide all error messages.
     */
    hideMessages: function() {
        this.dsPrompts.view().forEach(function(prompt) {
            prompt.set("validMessage", undefined);
        })
    },

    /**
     * Set the current prompts to use in this validator.
     * @param ds datasource containing prompts
     */
    setValidatorPrompts: function(ds) {
        this.dsPrompts = ds;
        this._minDate = moment(dateConvert.toDate(loginProps.get().minimumDateRange));
        this._maxDate = moment(dateConvert.toDate(loginProps.get().maximumDateRange));
        this._results = {};
    },

    /**
     * Called after server validation completes, to update validation results for each item.
     *
     * @param err validation exception
     * @param event original server event containing a fields array, containing deferred objects
     */
    _applyServerResults: function(err, event) {

        // for each validation result, find the corresponding deferred and reject it.
        // Then remove that item from the list.
        var me = this,
            model;

        err = err || {
            validationResults: {
                errors: []
            }
        };

        // there could be multiple validators on a single field.  Errors should always trump warnings.
        // We will step through each item in event.fields, and look for errors/warnings on that field.
        // for the remaining items in the list, resolve the deferred - it's valid.
        for (var field in event.fields) {
            // we should be guaranteed to have a corresponding model here - we put them in event.fields to begin with.
            model = $.grep(me.dsPrompts.view(), function(prompt) {
                return prompt.get("name") === field;
            })[0];

            var def = event.fields[field],
                valid = true,
                messages = [],
                state,
                errorIndices;

            // look for errors on this field.  Filter out VALID results even though the server should not return them.
            var serverResults = err.validationResults.errors.filter(function(err) {
                return err.fieldName === model.get(me.options.validationField) && err.errorType !== ValidState.VALID;
            });

            // process errors first, then warnings.
            serverResults.filter(function(err) {
                return err.errorType == ValidState.ERROR;
            }).forEach(function(result) {
                model.set("serverInvalid", true);
                valid = model.get("errorsOverridden");
                messages.push(result.errorMessage);
                state = result.errorType;
                errorIndices = result.errorIndices;
            });

            // if no errors, look for warnings
            if (messages.length == 0) {
                serverResults.filter(function(err) {
                    return err.errorType == ValidState.WARNING;
                }).forEach(function(result) {
                    messages.push(result.errorMessage);
                    state = result.errorType;
                    errorIndices = result.errorIndices;
                });
            }

            // if we found at least one error, start processing.
            // Otherwise, mark the field as valid.
            if (messages.length > 0) {
                model.set("serverInvalid", true);
                if (valid) {
                    def.resolve(state, messages);
                } else {
                    def.reject(state, messages, errorIndices);
                }
            } else {
                model.set("serverInvalid", false);

                // figure out whether the client was invalid for this field.
                // This fixes cases like blind entry where the server is ignorant of the blind validation.
                if (model.get("clientValid")) {
                    def.resolve(ValidState.VALID, "");
                } else {
                    def.reject(model.get("validState"), model.get("validMessage"), model.get("errorIndices"));
                }
            }
        }
    },

    // The default validation rules that are always applied to every field, regardless of whether they have DocFinity validators,
    // plus the DocFinity validators that can be run on the client side.
    // Returns an object containing a valid, message, and state properties.
    _clientRules: {

        // Required field validation
        required: function(model) {
            var value = this._getValue(model),
                ret = {
                    state: ValidState.VALID
                };

            if (model.get("required") && !this._skipRequired) {

                const setErrorState = () => {
                    let localeKey = "main.index.required";
                    const localeArgs = [model.get("label")];
                    const depends = this.dependencies.getMissingDependencies(model);
                    if(depends.length) {
                        localeKey = "main.index.required.dependencies";
                        localeArgs.push(join(depends));
                    }
                    ret.message = locale.get(localeKey, ...localeArgs);
                    ret.state = ValidState.ERROR;
                    ret.stop = true;
                };

                // figure out if we're dealing with a multi-value string.  You must provide at least 1 non-empty value.
                if (ObjectUtil.isArray(value)) {
                    if (value.length === 0) {
                        setErrorState();
                    }
                } else if (value === undefined || value === null || value === "") {
                    setErrorState();
                }

                // figure out if we're dealing with a checkbox. Checkboxes must have a TRUE value, not just any value, if required.
                if(model.get("inputType") === PromptInputType.SWITCH){
                    if(!model.get("value")){
                        setErrorState();
                    }
                }
            }
            return ret;
        },

        // string min length
        minLength: function(model) {
            var value = this._getValue(model),
                dataType = model.get("dataType"),
                min = model.get("minLength"),
                ret = {
                    state: ValidState.VALID,
                    errorIndices: []
                };
            if (value && dataType == DataType.STRING) {

                // figure out if we're working with multi-value strings
                if (!ObjectUtil.isArray(value)) {
                    value = [value];
                }

                value.forEach(function(val, idx) {
                    if (min > 0 && stringUtil.length(val) < min) {
                        ret.message = locale.get("main.index.minLength", model.get("label"), min);
                        ret.state = ValidState.ERROR;
                        ret.errorIndices.push(idx);
                    }
                });
            }
            return ret;
        },

        // string max length
        maxLength: function(model) {
            var value = this._getValue(model),
                dataType = model.get("dataType"),
                max = model.get("maxLength"),
                ret = {
                    state: ValidState.VALID,
                    errorIndices: []
                };
            if (value && dataType == DataType.STRING) {
                // NOTE: TextInput widgets should not allow typing more than max characters.
                // This is just a fail-safe.

                // figure out if we're working with multi-value strings
                if (!ObjectUtil.isArray(value)) {
                    value = [value];
                }

                value.forEach(function(val, idx) {
                    if (max > 0 && stringUtil.length(val) > max) {
                        ret.message = locale.get("main.index.maxLength", model.get("label"), max);
                        ret.state = ValidState.ERROR;
                        ret.errorIndices.push(idx);
                    }
                });
            }
            return ret;
        },

        // min date
        minDate: function(model) {
            var value = this._getValue(model),
                dataType = model.get("dataType"),
                ret = {
                    state: ValidState.VALID
                };
            if (value && dataType == DataType.DATE) {
                // validate that the date is within the min/max system property range.
                // DFDatePicker enforces that you cannot select dates outside the range, but you can still type dates manually.
                var date = moment(value);
                if (date.isBefore(this._minDate)) {
                    ret.message = locale.get("main.index.dateMin", model.get("label"), locale.formatDate(this._minDate.toDate()));
                    ret.state = ValidState.ERROR;
                }
            }
            return ret;
        },

        // max date
        maxDate: function(model) {
            var value = this._getValue(model),
                dataType = model.get("dataType"),
                ret = {
                    state: ValidState.VALID
                };
            if (value && dataType == DataType.DATE) {
                // validate that the date is within the min/max system property range.
                // DFDatePicker enforces that you cannot select dates outside the range, but you can still type dates manually.
                var date = moment(value);
                if (date.isAfter(this._maxDate)) {
                    ret.message = locale.get("main.index.dateMax", model.get("label"), locale.formatDate(this._maxDate.toDate()));
                    ret.state = ValidState.ERROR;
                }
            }
            return ret;
        },

        // invalid date format
        invalidDate: function(model) {
            var value = this._getValue(model),
                dataType = model.get("dataType"),
                ret = {
                    state: ValidState.VALID
                };
            if (!value && dataType == DataType.DATE) {
                // validate the date is formatted correctly.
                // DFDatePicker will set a rawValue on focus out in cases where the date is invalid.
                if (model.get("rawValue")) {
                    ret.message = locale.get("main.index.dateInvalid", model.get("label"), locale.getDateFormat().toUpperCase());
                    ret.state = ValidState.ERROR;
                }
            }
            return ret;
        },

        regexp: function(model) {
            var value = this._getValue(model),
                me = this,
                ret = {
                    state: ValidState.VALID,
                    errorIndices: []
                };
            if (value) {
                var validators = this._getValidatorsOfType(model, ValidatorType.REGEX);
                validators.forEach(function(validator) {
                    try {
                        var regexp = new RegExp(me._getProperty(validator, "regularExpression"));

                        // figure out if we're working with multi-value strings
                        if(!ObjectUtil.isArray(value)) {
                            value = [value];
                        }

                        value.forEach(function(val, idx) {
                            if(!regexp.test(val)) {
                                ret.state = validator.get("errorLevel");
                                ret.message = locale.get("main.index.regexp.error", model.get("label"), me._getProperty(validator, "errorMessage"));
                                ret.errorIndices.push(idx);
                            }
                        });
                    } catch(ex) {
                        throw new DisplayError(locale.get("validation.regex.runtime", model.get("label")));
                    }
                });
            }
            return ret;
        },

        blindEntry: function(model) {
            var ret = {
                state: ValidState.VALID
            };
            if (model.get("blindEntry")) {
                if (model.get("value") !== model.get("blindValue")) {
                    ret.state = ValidState.ERROR;
                    ret.message = locale.get("main.index.blindEntry.error", model.get("label"));
                }
            }
            return ret;
        },

        /**
         * Validates that the model's value is greater than or equal to the java integer minimum
         * @param model
         * @returns {{state: string, errorIndices: Array}}
         */
        minInteger: function(model) {
            var minInt = -2147483648;
            var value = this._getValue(model);
            var dataType = model.get("dataType");
            var ret = {
                state: ValidState.VALID,
                errorIndices: []
            };
            if (value && (dataType === DataType.INTEGER || dataType === DataType.DECIMAL) && value < minInt) {
                ret.message = locale.get("validation.generic.min", model.get("label"), minInt);
                ret.state = ValidState.ERROR;
            }
            return ret;
        },

        /**
         * Validates that the model's value is less than or equal to the java integer maximum
         * @param model
         * @returns {{state: string, errorIndices: Array}}
         */
        maxInteger: function(model) {
            var maxInt = 2147483647;
            var value = this._getValue(model);
            var dataType = model.get("dataType");
            var ret = {
                state: ValidState.VALID,
                errorIndices: []
            };
            if (value && (dataType === DataType.INTEGER || dataType === DataType.DECIMAL) && value > maxInt) {
                ret.message = locale.get("validation.generic.max", model.get("label"), maxInt);
                ret.state = ValidState.ERROR;
            }
            return ret;
        }
    },

    /**
     * Validation rules that can only be run by validating the server.  Each method simply returns true if it's
     * applicable to the specified model.
     */
    _serverRules: {

        sql: function(model) {
            return this._getValue(model) && this._hasValidatorOfType(model, ValidatorType.SQL);
        },

        stringCompare: function(model) {
            return this._getValue(model) && this._hasValidatorOfType(model, ValidatorType.STRING_COMPARE);
        },

        dateCompare: function(model) {
            return this._getValue(model) && this._hasValidatorOfType(model, ValidatorType.DATE_COMPARE);
        },

        decimalCompare: function(model) {
            return this._hasValue(model) && this._hasValidatorOfType(model, ValidatorType.DECIMAL_COMPARE);
        },

        integerCompare: function(model) {
            return this._hasValue(model) && this._hasValidatorOfType(model, ValidatorType.INTEGER_COMPARE);
        },

        mask: function(model) {
            return this._getValue(model) && this._hasValidatorOfType(model, ValidatorType.MASK);
        }
    },

    _hasValue(model) {
        const value = this._getValue(model);
        return value !== undefined && value !== null && value !== "";
    },

    _getValidatorsOfType: function(model, type) {
        var ret = [],
            validators = model.get("validators");
        if (validators) {
            ret = $.grep(validators, function(validator) {
                return validator.get("validatorType") === type;
            });
        }
        return ret;
    },

    _hasValidatorOfType: function(model, type) {
        return this._getValidatorsOfType(model, type).length > 0
    },

    _getProperty: function(validator, name) {
        return $.grep(validator.get("properties"), function(property) {
            return property.get("name") === name;
        })[0].value;
    },

    /**
     * Get the current value of a prompt model.  Trims string values.  Removes empty strings from multi-value string values.
     * @param model input model
     * @return {*}
     * @private
     */
    _getValue: function(model) {
        var value = model.get("value");
        if (value && model.get("dataType") == DataType.STRING) {
            if (value.trim) {
                value = value.trim();
            } else if (ObjectUtil.isArray(value)) {
                for (var i = 0; i < value.length; ++i) {
                    value[i] = value[i].trim ? value[i].trim() : value[i];
                }

                // always trim out empty strings.
                value = value.filter(function(val) {
                    return !!val;
                });
            }
        }
        return value;
    }
};
